import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import { maintenanceAPI, propertyAPI, propertyUnitAPI } from '../../services/api';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';

const MaintenanceListPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [form, setForm] = useState({ property_id: '', property_unit_id: '', category_id: '', title: '', description: '', priority: 'medium', estimated_cost: '' });
  const navigate = useNavigate();

  const fetchTickets = () => maintenanceAPI.list({ status: filter || undefined }).then(r => { setTickets(r.data); setLoading(false); }).catch(() => setLoading(false));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTickets(); }, [filter]);

  const openCreate = async () => {
    try {
      const [cRes, pRes] = await Promise.all([maintenanceAPI.getCategories(), propertyAPI.list({ limit: 100 })]);
      setCategories(cRes.data);
      setProperties(pRes.data.data || []);
    } catch (e) {}
    setShowModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.estimated_cost) payload.estimated_cost = parseFloat(payload.estimated_cost);
    else delete payload.estimated_cost;
    if (!payload.category_id) delete payload.category_id;
    if (!payload.property_unit_id) delete payload.property_unit_id;
    await maintenanceAPI.create(payload);
    setShowModal(false);
    fetchTickets();
  };

  // Chargement des unités quand le bien change
  const handlePropertyChange = async (propertyId) => {
    const prop = properties.find(p => String(p.id) === String(propertyId));
    setSelectedProperty(prop || null);
    setForm(f => ({ ...f, property_id: propertyId, property_unit_id: '' }));
    setUnits([]);
    if (prop?.is_multi_unit && propertyId) {
      setLoadingUnits(true);
      try {
        const r = await propertyUnitAPI.listByProperty(propertyId);
        setUnits(r.data || []);
      } catch (e) {
        setUnits([]);
      } finally {
        setLoadingUnits(false);
      }
    }
  };

  const priorityBadge = (p) => ({ urgent: 'danger', high: 'warning', medium: 'info', low: 'secondary' }[p] || 'secondary');
  const statusBadge = (s) => ({ open: 'danger', in_progress: 'warning', completed: 'success', closed: 'secondary' }[s] || 'secondary');

  const columns = [
    { header: 'N° Ticket', accessor: 'ticket_number' },
    { header: 'Titre', accessor: 'title' },
    { header: 'Bien', accessor: 'property_title' },
    { header: 'Catégorie', accessor: 'category_name' },
    { header: 'Priorité', render: (r) => <span className={`badge badge-${priorityBadge(r.priority)}`}>{r.priority}</span> },
    { header: 'Statut', render: (r) => <span className={`badge badge-${statusBadge(r.status)}`}>{r.status}</span> },
    { header: 'Assigné à', render: (r) => r.provider_name || (r.assigned_first_name ? `${r.assigned_first_name} ${r.assigned_last_name}` : '-') },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Maintenance</h1>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Nouveau ticket</button>
      </div>
      <div className="filters">
        {['', 'open', 'in_progress', 'completed', 'closed'].map(s => (
          <button key={s} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(s)}>
            {s === '' ? 'Tous' : s === 'open' ? 'Ouverts' : s === 'in_progress' ? 'En cours' : s === 'completed' ? 'Terminés' : 'Fermés'}
          </button>
        ))}
      </div>
      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <div className="card"><DataTable columns={columns} data={tickets} onRowClick={(r) => navigate(`/maintenance/${r.id}`)} emptyMessage="Aucun ticket" /></div>
      )}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Nouveau ticket de maintenance">
        <form onSubmit={handleCreate}>
          <div className="form-group"><label>Bien *</label>
            <select
              className="form-control"
              value={form.property_id}
              onChange={e => handlePropertyChange(e.target.value)}
              required
            >
              <option value="">Sélectionner</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>
                  {p.title}{p.is_multi_unit ? ' 🏢' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Sélection d'unité si le bien est multi-unités */}
          {selectedProperty?.is_multi_unit && (
            <div className="form-group" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px' }}>
              <label style={{ color: '#1d4ed8', fontWeight: 600 }}>Unité concernée (optionnel)</label>
              {loadingUnits ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13 }}>
                  <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                  Chargement...
                </div>
              ) : (
                <select
                  className="form-control"
                  value={form.property_unit_id}
                  onChange={e => setForm({ ...form, property_unit_id: e.target.value })}
                >
                  <option value="">Immeuble entier (toutes unités)</option>
                  {units.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.unit_number} — {u.unit_type}{u.surface_area ? ` · ${u.surface_area}m²` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          <div className="form-group"><label>Titre *</label><input className="form-control" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required /></div>
          <div className="grid-2">
            <div className="form-group"><label>Catégorie</label>
              <select className="form-control" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                <option value="">Sélectionner</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Priorité</label>
              <select className="form-control" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                <option value="low">Basse</option><option value="medium">Moyenne</option><option value="high">Haute</option><option value="urgent">Urgente</option>
              </select>
            </div>
          </div>
          <div className="form-group"><label>Description</label><textarea className="form-control" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div className="form-group"><label>Coût estimé</label><input type="number" className="form-control" value={form.estimated_cost} onChange={e => setForm({ ...form, estimated_cost: e.target.value })} /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary">Créer</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MaintenanceListPage;
